import EditNoteIcon from '@mui/icons-material/EditNote';
import TimelineRecordAuthorWithAccessCheck from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordAuthorWithAccessCheck';
import { type FilterBarFilterConfig } from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import type { TimelineConfig } from 'framework/ViewTypes/embeddable';
import { POSITION_PROFILE_TIMELINE_TYPES } from '../../../constants';
import type { PositionProfileTimelineEvent, PositionProfileTimelineEventType } from '../../../types';
import { PositionProfileTimelineNoteAddForm } from './forms/PositionProfileTimelineNoteForm';

const EVENT_TITLES: Record<PositionProfileTimelineEventType, string> = {
  CREATED: 'Position profile created.',
  UPDATED: 'Position profile updated.',
  DEPRECATED: 'Position profile deprecated.',
  REACTIVATED: 'Position profile reactivated.',
  RETIRED: 'Position profile retired.',
  NOTE: 'Note added to position profile.',
};

const EVENT_LABELS: Record<PositionProfileTimelineEventType, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  DEPRECATED: 'Deprecated',
  REACTIVATED: 'Reactivated',
  RETIRED: 'Retired',
  NOTE: 'Note',
};

export const TIMELINE_FILTERS: FilterBarFilterConfig[] = [
  {
    id: 'type',
    source: 'type',
    condition: FilterCondition.IN,
    type: 'dropdown',
    label: 'Activity type',
    multiple: true,
    options: Object.entries(EVENT_LABELS).map(([value, label]) => ({ value, label })),
  },
];

const mapTimelineEvent = (event: PositionProfileTimelineEvent) => ({
  id: event.id,
  type: event.type,
  occurredAt: event.createdAt,
  actor: event.actorAlias,
});

export const getPositionProfileTimelineConfig = (
  canManageNotes: boolean,
): TimelineConfig<PositionProfileTimelineEvent> => ({
  RecordAuthor: TimelineRecordAuthorWithAccessCheck,
  types: {
    [POSITION_PROFILE_TIMELINE_TYPES.CREATED]: {
      mapItem: event => ({ ...mapTimelineEvent(event), description: EVENT_TITLES.CREATED }),
    },
    [POSITION_PROFILE_TIMELINE_TYPES.UPDATED]: {
      mapItem: event => ({ ...mapTimelineEvent(event), description: EVENT_TITLES.UPDATED }),
    },
    [POSITION_PROFILE_TIMELINE_TYPES.DEPRECATED]: {
      mapItem: event => ({ ...mapTimelineEvent(event), description: EVENT_TITLES.DEPRECATED }),
    },
    [POSITION_PROFILE_TIMELINE_TYPES.REACTIVATED]: {
      mapItem: event => ({ ...mapTimelineEvent(event), description: EVENT_TITLES.REACTIVATED }),
    },
    [POSITION_PROFILE_TIMELINE_TYPES.RETIRED]: {
      mapItem: event => ({ ...mapTimelineEvent(event), description: EVENT_TITLES.RETIRED }),
    },
    [POSITION_PROFILE_TIMELINE_TYPES.NOTE]: {
      appearance: {
        icon: <EditNoteIcon />,
        iconCircleColor: 'primary.main',
        headerBgColor: 'background.primary',
      },
      mapItem: event => ({
        ...mapTimelineEvent(event),
        description: event.note,
      }),
      add: {
        tag: 'Add note',
        tagColor: 'primary',
        Form: PositionProfileTimelineNoteAddForm,
        canAdd: canManageNotes,
      },
    },
  },
});
